import { getHCStudentActivitiesByGroup } from '@amplify/instruction-groups';
import get from 'lodash.get';
import flatMap from 'lodash.flatmap';
import uniq from 'lodash.uniq';
import { isBefore, parseISO, parse, subHours, format, compareDesc } from 'date-fns';
import isNil from 'lodash.isnil';
import compact from 'lodash.compact';
import { getAvailableMeasures, getAvailableStatuses } from '../../features/homeConnect/hcHelper';
import {
    getColumns,
    getColumnTitles,
} from '../../features/pdfGeneration/shared/allStudentsInClass/utils';
import {
    GATING,
    ESPANOL_APPLICATION_NAME,
    LANGUAGE,
    LICENSE,
    LOGO_MAPPER,
    MCLASS_PRODUCT,
    PERIOD,
    TITLE,
} from '../../constants/shared/CommonConstants';
import { MEASURE } from '../../constants/shared/MeasuresConstants';
import {
    getSortedResultsByDate,
    sortByPredefinedOrder,
    sortStudentsByName,
} from '../../utils/shared/sortUtils';
import {
    getPmStatusesByPeriod,
    getStudentListOfGradeYears,
    getStudentResultByMeasureYearGradePeriod,
    getStudentResultsByMeasureYearGradePeriod,
    getStudentsByGrade,
    getStudentStatusByYearGrade,
} from '../../utils/shared/filterUtils';
import { buildTrcBooks } from '../../features/studentSummary/shared/trcSummary/trcContent/trcTables/trcTablesUtils';
import {
    getIntercomLicensingInfo,
    getReportType,
    getRootInstitutionsInfo,
} from '../../utils/shared/intercomUtils';
import {
    buildStudentProfileDescriptionKey,
    getGroupsList,
    getOverallSkillsInterps,
    buildOverallStatuses,
    getParsedSkillsData,
    getStudentProfileDescriptionBySkillKey,
    getGradeSkillsByMeasures,
    getGradeMeasuresFromGradeAvailabilityInfo,
} from '../../utils/shared/dualLanguageUtils';
import {
    getMeasuresByGrade,
    isMeasureAvailableInGrade,
} from '../../utils/shared/gradeToMeasureUtils';
import { GRADE, ALL_GRADES as ALL_GRADES_MAPPER } from '../../constants/shared/GradesConstants';
import { buildBestStatuses } from '../../utils/shared/instructionUtils';
import { LICENSE as READING_LICENSE } from '../../constants/reading/CommonConstants';
import isStandaloneExpress from '../../utils/reading/expressUtils';
import { getZoGGridData } from '../../utils/shared/zogUtils';
import {
    getCompositeAchievedGrowthRateLevel,
    getGrowthAnalysisData,
    getStudentOverallObject,
    isFullYear,
} from '../../utils/shared/growthAnalysisUtils';
import { ACHIEVED_RATE } from '../../constants/shared/GrowthAnalysisConstants';

const { TEXAS, NORTH_CAROLINA, D8, LOUISIANA } = LICENSE;
const { EXPRESS } = READING_LICENSE;
const { SUMMARY, MOY } = PERIOD;
const { OVERALL, TRC } = MEASURE;
const { ALL_GRADES } = GRADE;
const { ENGLISH, SPANISH } = LANGUAGE;
const { TESTED_OUT } = GATING;
const { YEAR_GROWTH_RATE, GROWTH_RATE } = ACHIEVED_RATE;

export const getStudentInfo = ({
    firstName,
    lastName,
    externalId: id,
    homeLanguage,
    tk: isTransitionalKindergarten,
    temporalId,
    temporalInstId,
    primaryId,
}) => ({
    id,
    firstName,
    lastName,
    homeLanguage,
    isTransitionalKindergarten,
    temporalId,
    temporalInstId,
    primaryId,
});

export const getHomeConnectStudentsData = (
    selectedPeriod,
    currentPeriod,
    allStudentsStatuses,
    gradeAvailability,
    overallMeasures,
    hcMeasureTitles,
) => {
    const period = selectedPeriod === SUMMARY ? currentPeriod : selectedPeriod;
    const hcAvailableMeasuresKeys = Object.keys(hcMeasureTitles);
    let filteredGradeAvailability = gradeAvailability;

    if (period === PERIOD.BOY) {
        filteredGradeAvailability = gradeAvailability.map(detail => ({
            ...detail,
            measures:
                detail.key === GRADE.KINDERGARTEN
                    ? detail.measures.filter(measure => measure !== MEASURE.SPELLING)
                    : detail.measures,
        }));
    }

    const studentRows = allStudentsStatuses.map(({ studentInfo, grade, statuses }) => {
        const statusesByPeriod = statuses.filter(status => status.period === period);
        const allAvailableMeasuresInGrade = sortByPredefinedOrder(
            get(
                filteredGradeAvailability.find(({ key }) => key === grade),
                'measures',
                [],
            ),
            hcAvailableMeasuresKeys,
        );
        const availableMeasures = allAvailableMeasuresInGrade
            .filter(measure => !overallMeasures.includes(measure) || measure === OVERALL)
            .map(measure => ({
                key: measure,
                title: hcMeasureTitles[measure],
            }));
        const availableMeasuresByStatuses = getAvailableMeasures(
            statusesByPeriod,
            availableMeasures,
        );

        return {
            studentInfo: { ...studentInfo, grade },
            availableMeasures: availableMeasuresByStatuses,
            isEnglishChecked: false,
            isSpanishChecked: false,
        };
    });

    return studentRows.sort(sortStudentsByName);
};

const excludeSpellingForGradeKBOYPeriod = (
    HOME_CONNECT_MEASURES,
    HOME_CONNECT_LETTER_MEASURES,
    grade,
    period,
) => {
    let homeConnectMeasure = HOME_CONNECT_MEASURES;
    let homeConnectLetterMeasure = HOME_CONNECT_LETTER_MEASURES;
    if (grade === GRADE.KINDERGARTEN && period === PERIOD.BOY) {
        homeConnectMeasure = homeConnectMeasure.filter(measure => measure.key !== MEASURE.SPELLING);
        homeConnectLetterMeasure = homeConnectLetterMeasure.filter(
            measure => measure !== MEASURE.SPELLING,
        );
    }
    return {
        homeConnectMeasure,
        homeConnectLetterMeasure,
    };
};

export const getHomeConnectPDFStudentsData = (
    selectedPeriod,
    currentPeriod,
    interps,
    allStudentsStatuses,
    GROUP_TYPE,
    HOME_CONNECT_MEASURES,
    HOME_CONNECT_LETTER_MEASURES,
    gradeToMeasureMapper,
    measureMapper = () => {},
) => {
    const period = selectedPeriod === SUMMARY ? currentPeriod : selectedPeriod;
    const studentRows = allStudentsStatuses.map(({ studentInfo, grade, statuses }) => {
        const allAvailableMeasuresInGrade = getMeasuresByGrade(gradeToMeasureMapper, grade);
        const statusesByPeriod = statuses.filter(status => status.period === period);

        const { homeConnectMeasure, homeConnectLetterMeasure } = excludeSpellingForGradeKBOYPeriod(
            HOME_CONNECT_MEASURES,
            HOME_CONNECT_LETTER_MEASURES,
            grade,
            period,
        );
        const availableMeasures = getAvailableMeasures(statusesByPeriod, homeConnectMeasure).filter(
            ({ key }) => allAvailableMeasuresInGrade.includes(key),
        );
        const statusesWithInterpsData = getAvailableStatuses(
            statusesByPeriod,
            availableMeasures,
            interps,
            OVERALL,
            homeConnectLetterMeasure,
            measureMapper,
        );
        const activitiesIds = getHCStudentActivitiesByGroup(
            grade,
            period,
            statusesWithInterpsData,
            GROUP_TYPE,
        );

        return {
            studentInfo: { ...studentInfo, grade },
            availableMeasures,
            statusesWithInterpsData,
            activitiesIds,
            isEnglishChecked: false,
            isSpanishChecked: false,
        };
    });
    return studentRows.sort(sortStudentsByName);
};

export const getStudentStatusesById = (results, studentId) =>
    get(results, `${studentId}.statuses`, []);

export const getInstructionStatusesDataByStudent = (
    studentsResults,
    student,
    currentYear,
    checkPointPeriod,
    selectedDate,
    windowDates,
    pmThresholdsMapper,
    toyPeriods,
) => {
    const statusesBeforeCheckPoint = getStudentStatusesById(
        studentsResults,
        student.externalId,
    ).filter(
        status =>
            status.schoolYear === currentYear &&
            isBefore(parseISO(status.date), parseISO(selectedDate)) &&
            (!status.isSecure || status.measure === OVERALL),
    );
    const isSecureFound = getStudentStatusesById(studentsResults, student.externalId).some(
        status => status.isSecure,
    );
    const bmStatusesBeforeCheckPoint = statusesBeforeCheckPoint.filter(
        status =>
            status.period === checkPointPeriod && (!status.isSecure || status.measure === OVERALL),
    );
    const pmStatusesBeforeCheckPointByDesc = getSortedResultsByDate(
        getPmStatusesByPeriod(statusesBeforeCheckPoint, checkPointPeriod, windowDates, toyPeriods),
    );
    const bestStatuses = buildBestStatuses(
        bmStatusesBeforeCheckPoint.concat(pmStatusesBeforeCheckPointByDesc),
        checkPointPeriod,
        pmThresholdsMapper,
    );
    return {
        bmStatuses: bmStatusesBeforeCheckPoint,
        bestStatuses,
        isSecureFound,
    };
};

export const getAllStudentsInClassData = (
    measureListForPDF,
    studentsData,
    interps,
    currentSchoolYear,
    MEASURES_KEYS,
    gradeToMeasureMapper,
    getGoalSection,
    toyPeriods,
    summerPeriodLabel,
    isLouisiana = false,
) =>
    studentsData.map(({ statuses, grade, listOfGrades, studentInfo }) => {
        const columnTitles = getColumnTitles(currentSchoolYear, listOfGrades, summerPeriodLabel);
        const studentMeasures = sortByPredefinedOrder(
            uniq(
                flatMap(
                    listOfGrades.map(gradeYear =>
                        measureListForPDF.filter(measure =>
                            isMeasureAvailableInGrade(gradeToMeasureMapper, gradeYear, measure),
                        ),
                    ),
                ),
            ),
            MEASURES_KEYS,
        );

        const resultsByMeasure = studentMeasures.map(measure => ({
            measure,
            columns: getColumns(
                listOfGrades,
                interps,
                statuses,
                measure,
                currentSchoolYear,
                gradeToMeasureMapper,
                getGoalSection,
                toyPeriods,
                grade,
                summerPeriodLabel,
                isLouisiana,
            ),
        }));

        const hasSummerPeriod = toyPeriods.includes(PERIOD.SUMMER);
        const isThirdGrade = grade === GRADE.THIRD_GRADE;
        const isSummerPeriodPresentForStudent = isLouisiana
            ? isThirdGrade && hasSummerPeriod
            : hasSummerPeriod;

        const gradeColumnsTitlesWithPeriods = columnTitles.map((title, index) => {
            const periods = resultsByMeasure[0]
                ? get(resultsByMeasure[0].columns[index], 'cells', []).map(({ period }) => period)
                : [];

            return {
                gradeColumnTitle: title,
                periods,
                isSummerPeriodPresent:
                    periods.includes(TITLE.SUMMER_TITLE) ||
                    periods.includes(TITLE.SUMMER_LDOE_TITLE),
            };
        });
        return {
            gradeColumnsTitlesWithPeriods,
            resultsByMeasure,
            studentMeasures,
            grade,
            studentInfo,
            isSummerPeriodPresentForStudent,
        };
    });

export const getAllStudentsStatuses = (studentsByGrade, results, year) =>
    flatMap(
        studentsByGrade.map(({ grade, students }) =>
            students.map(student => {
                return {
                    studentInfo: getStudentInfo(student),
                    grade,
                    statuses: getStudentResultsByMeasureYearGradePeriod(
                        getStudentStatusesById(results, student.externalId),
                        null,
                        year,
                        grade,
                    ),
                };
            }),
        ),
        studentData => [...studentData],
    );

export const getAllStudentSummaryStatuses = (results, allStudentsGradeYears, studentsByGrade) =>
    flatMap(
        studentsByGrade.map(({ grade, students }) =>
            students.map(student => ({
                studentInfo: getStudentInfo(student),
                grade,
                listOfGrades: getStudentListOfGradeYears(allStudentsGradeYears[student.externalId]),
                statuses: getStudentResultsByMeasureYearGradePeriod(
                    getStudentStatusesById(results, student.externalId),
                ),
            })),
        ),
        studentData => [...studentData],
    );

export const getSelectedGradeCheckPointObject = (
    checkPoints,
    selectedInstructionGrade,
    isSecureFound,
    windowDates,
) => {
    const selectedCheckPointGradeData = get(checkPoints, selectedInstructionGrade, []);

    const selectedCheckPointObject = {
        dates: [],
        dateToPeriod: {},
    };

    // for secure period to show data by previous day of EOY start date
    const currentDate = parse(windowDates.startDate, 'MM/dd/yyyy', new Date());
    const previousDay = format(subHours(currentDate, 12), "yyyy-MM-dd'T'HH:mm:ss'Z'");

    selectedCheckPointGradeData.forEach(dataItem => {
        const { period } = dataItem;
        dataItem.dates.forEach(dateItem => {
            selectedCheckPointObject.dates.push(dateItem);
            selectedCheckPointObject.dateToPeriod[dateItem] = period;
        });
    });

    // adding "one day before EOY" date for display instructions based on results of "MOY + MOY_PM" periods
    if (isSecureFound) {
        selectedCheckPointObject.dates.push(previousDay);
        selectedCheckPointObject.dateToPeriod[previousDay] = MOY;
        const dateObjects = selectedCheckPointObject.dates.map(dateString => ({
            dateString,
            dateObject: parseISO(dateString),
        }));
        dateObjects.sort((a, b) => compareDesc(a.dateObject, b.dateObject));
        selectedCheckPointObject.dates = dateObjects.map(({ dateString }) => dateString);
    }

    return selectedCheckPointObject;
};

export const getTrcSummaryData = (results, studentId, selectedGradeYear, toyPeriods) => {
    const studentResults = get(results[studentId], 'results');
    const { year, grade } = selectedGradeYear;
    return toyPeriods.map(period => {
        const trcResult = getStudentResultByMeasureYearGradePeriod(
            studentResults,
            TRC,
            year,
            grade,
            period,
        );

        return trcResult
            ? { period, result: trcResult, books: buildTrcBooks(trcResult) }
            : { period };
    });
};

export const getSharedIntercomInfo = (
    location,
    firstName,
    lastName,
    intercomData,
    isIntercomInfoFetched,
    views,
) => {
    const { instName, instId, userEmail, rootInstitutions, products, name } = intercomData;
    const { institutionsIds, institutionNames } =
        isIntercomInfoFetched && getRootInstitutionsInfo(rootInstitutions);
    const licensingInfo = isIntercomInfoFetched && getIntercomLicensingInfo(products);

    return {
        email: userEmail,
        user_id: userEmail,
        firstName,
        lastName,
        name,
        inst_name: instName,
        inst_id: instId,
        [MCLASS_PRODUCT]: licensingInfo,
        root_inst_id: institutionsIds,
        root_inst_name: institutionNames,
        mclass_report_type: getReportType(location, views),
    };
};

export const getDualLanguageStudentData = (
    student,
    results,
    year,
    period,
    selectedGrade,
    config,
) => {
    const studentInfo = getStudentInfo(student);
    const statuses = get(results, `${studentInfo.id}.statuses`, []);
    const additionalStatuses = get(results, `${studentInfo.id}.additionalStatuses`, []);

    const { d8OverallInterp, trcEnglishInterp, lecturaOverallInterp, trcSpanishInterp } =
        getOverallSkillsInterps(
            config.statusesLanguage === LANGUAGE.ENGLISH ? statuses : additionalStatuses,
            config.additionalStatusesLanguage === LANGUAGE.ENGLISH ? statuses : additionalStatuses,
            year,
            selectedGrade,
            period,
        );
    const overallStatuses = buildOverallStatuses(
        d8OverallInterp,
        trcEnglishInterp,
        lecturaOverallInterp,
        trcSpanishInterp,
    );
    const parsedSkillsData = getParsedSkillsData({
        statuses,
        additionalStatuses,
        selectedYear: year,
        selectedGrade,
        selectedPeriod: period,
        config,
    });

    return {
        studentInfo,
        overallStatuses,
        overallDescription: getStudentProfileDescriptionBySkillKey(
            OVERALL,
            buildStudentProfileDescriptionKey(...overallStatuses),
            studentInfo.firstName,
        ).description,
        groups: getGroupsList(
            parsedSkillsData.skills,
            parsedSkillsData.results,
            studentInfo.firstName,
            selectedGrade,
        ),
        skills: parsedSkillsData.skills,
        measureResults: parsedSkillsData.results,
    };
};

export const getDualLanguageClassSummaryPdfData = (
    studentsByGrade,
    results,
    year,
    period,
    config,
) => {
    const availableGrades = studentsByGrade.map(({ grade }) => grade);
    const { gradeAvailabilityInfo, additionalGradeAvailabilityInfo } = config;

    return {
        selectedPeriod: period,
        rows: availableGrades.map(grade => ({
            grade,
            skills: getGradeSkillsByMeasures(
                uniq(
                    getGradeMeasuresFromGradeAvailabilityInfo(gradeAvailabilityInfo, grade).concat(
                        getGradeMeasuresFromGradeAvailabilityInfo(
                            additionalGradeAvailabilityInfo,
                            grade,
                        ),
                    ),
                ),
                grade,
            ),
            students: getStudentsByGrade(studentsByGrade, grade).map(student => {
                const { studentInfo, overallStatuses, measureResults } = getDualLanguageStudentData(
                    student,
                    results,
                    year,
                    period,
                    grade,
                    config,
                );

                const statuses = get(results, `${studentInfo.id}.statuses`, []);
                const additionalStatuses = get(results, `${studentInfo.id}.additionalStatuses`, []);
                const overallStatus = getStudentResultByMeasureYearGradePeriod(
                    statuses,
                    OVERALL,
                    year,
                    grade,
                    period,
                );
                const overallAdditionalStatus = getStudentResultByMeasureYearGradePeriod(
                    additionalStatuses,
                    OVERALL,
                    year,
                    grade,
                    period,
                );
                return {
                    studentInfo,
                    overallStatuses: overallStatuses.reduce(
                        (acc, currentStatus) => ({
                            ...acc,
                            [currentStatus.language]: {
                                ...currentStatus,
                                status:
                                    config.statusesLanguage === currentStatus.language
                                        ? overallStatus
                                        : overallAdditionalStatus,
                            },
                        }),
                        {},
                    ),
                    measureResults: measureResults[ENGLISH].reduce((acc, currentResult, index) => {
                        acc.push({
                            [ENGLISH]: currentResult,
                            [SPANISH]: measureResults[SPANISH][index],
                        });
                        return acc;
                    }, []),
                };
            }),
        })),
    };
};

export const getTotalNumberOfStudents = (studentIds, studentsByGrade, selectedGrade) =>
    selectedGrade === ALL_GRADES
        ? studentIds.length
        : get(
              studentsByGrade.find(student => student.grade === selectedGrade),
              'students',
              [],
          ).length;

export const getStudentIds = (results, year, grade) => {
    const studentsIds = [];
    Object.keys(results).forEach(studentId => {
        if (!isNil(getStudentStatusByYearGrade(results, studentId, year, grade))) {
            studentsIds.push(studentId);
        }
    });
    return studentsIds;
};

export const getStaffIdsFromStudentsBasicComprehensionResults = studentsByGrade => {
    const staffIds = [];
    studentsByGrade.forEach(item => {
        item.studentsData.forEach(student => {
            if (student.bmResult) {
                staffIds.push(student.bmResult.assessor);
            }
            if (student.pmResult) {
                staffIds.push(student.pmResult.assessor);
            }
        });
    });
    return compact(staffIds);
};

export const getAppLogo = (licensingInfo, applicationName = '') => {
    if (licensingInfo.includes(NORTH_CAROLINA)) return LOGO_MAPPER[NORTH_CAROLINA];
    if (licensingInfo.includes(TEXAS)) return LOGO_MAPPER[TEXAS];
    if (licensingInfo.includes(LOUISIANA) && applicationName !== ESPANOL_APPLICATION_NAME)
        return LOGO_MAPPER[LOUISIANA];

    return isStandaloneExpress(licensingInfo) ? LOGO_MAPPER[EXPRESS] : LOGO_MAPPER[D8];
};

const isClassroomReportingLink = sublinks =>
    sublinks?.some(({ linkUrl }) => linkUrl.includes('classroom-reports'));

const isCurrentLink = linkUrl => window.location.pathname.includes(linkUrl);

const getLinkConfig = (linkUrl, sublinks, text, openInNewTab) =>
    isClassroomReportingLink(sublinks)
        ? {
              text,
              sublinks: sublinks.map(
                  ({ linkUrl: sublinkUrl, text: subLinkText, openInNewTab: sublinkNewTab }) => ({
                      linkUrl: sublinkUrl,
                      text: subLinkText,
                      ...(isCurrentLink(sublinkUrl) ? { onLinkClick: () => {} } : {}),
                      openInNewTab: sublinkNewTab,
                  }),
              ),
          }
        : { linkUrl, text, openInNewTab };

export const getAmpNavConfig = staffConfig => ({
    appName: 'classroom-reports',
    programName: 'mCLASS',
    typeofSubnav: 'subnav',
    oldRRD: true,
    tabLikeMainNav: true,
    mainNavTabs:
        staffConfig?.appLinks?.ampNavLinks.map(({ linkUrl, sublinks, text, openInNewTab }) =>
            getLinkConfig(linkUrl, sublinks, text, openInNewTab),
        ) || [],
    adultsView: true,
});

export const getHistoricalGradeToMeasuresMapper = (
    gradeToMeasureMapper,
    getExtendableMeasuresAvailabilityInGrade,
    ALL_MEASURES,
) => {
    if (isNil(gradeToMeasureMapper)) return [];

    const allAvailableMeasures = uniq(
        gradeToMeasureMapper.reduce((acc, { measures }) => {
            acc.push(...measures);

            return acc;
        }, []),
    );
    const extendedMeasuresAvailabilityMapper =
        getExtendableMeasuresAvailabilityInGrade(gradeToMeasureMapper);

    return ALL_GRADES_MAPPER.map(grade => ({
        key: grade,
        measures: ALL_MEASURES.filter(
            measure =>
                extendedMeasuresAvailabilityMapper[measure].includes(grade) &&
                allAvailableMeasures.includes(measure),
        ),
    }));
};

export const getStudentGoal = ({
    measure,
    period,
    statuses,
    interps,
    growthZones,
    customGoal,
    getStrugglingAdvancedGoalForMeasure,
    appName = '',
}) => {
    return !isNil(customGoal)
        ? customGoal.goalValue
        : getStrugglingAdvancedGoalForMeasure(
              measure,
              period,
              statuses,
              interps,
              growthZones,
              appName,
          );
};

export const getStudentsZoGDataByGrades = (
    studentsByGrade,
    results,
    interps,
    goals,
    compositeGoals,
    year,
    pmPeriod,
    getGradeToMeasureMapper,
    getMeasureByGrade,
) => {
    return studentsByGrade.map(({ grade, students }) => ({
        grade,
        studentsData: students.map(student => {
            const { externalId: id } = student;
            const studentInfo = getStudentInfo(student);
            const statuses = getStudentStatusesById(results, id);
            const growthZones = get(results, `${id}.growthZones`, []);
            const studentGoals = get(goals, `${id}`, []);
            const compositeGoal = get(compositeGoals, `${id}`, 0);
            const data = getZoGGridData({
                statuses,
                growthZones,
                interps,
                studentGoals,
                compositeGoal,
                year,
                grade,
                pmPeriod,
                getGradeToMeasureMapper,
                getMeasureByGrade,
            });

            return { studentInfo, data };
        }),
    }));
};

export const getStudentsGrowthAnalysisDataByGrades = (
    studentsByGrade,
    results,
    interps,
    goals,
    year,
    startPeriod,
    endPeriod,
    getGradeToMeasureMapper,
    getMeasureByGrade,
) =>
    studentsByGrade.map(({ grade, students }) => ({
        grade,
        studentsData: students.map(student => {
            const { externalId: id } = student;
            const growthZones = get(results, `${id}.growthZones`, []);
            const zone = getStudentResultByMeasureYearGradePeriod(
                growthZones,
                OVERALL,
                year,
                null,
                endPeriod,
            );
            const statuses = getStudentStatusesById(results, id);
            const studentGoals = getStudentResultsByMeasureYearGradePeriod(
                get(goals, `${id}`, []),
                null,
                year,
                grade,
                endPeriod,
            );
            const data = getGrowthAnalysisData({
                statuses,
                growthZones,
                interps,
                studentGoals,
                year,
                grade,
                startPeriod,
                endPeriod,
                getGradeToMeasureMapper,
                getMeasureByGrade,
            });
            const isWholeYear = isFullYear(startPeriod, endPeriod);
            const currentComposite = getStudentOverallObject(results, id, year, startPeriod, grade);
            const nextComposite = getStudentOverallObject(results, id, year, endPeriod, grade);
            const isTestedOutAvailable = data.find(
                dataByMeasure =>
                    !isNil(dataByMeasure.statusByEndPeriod) &&
                    dataByMeasure.statusByEndPeriod.gatingType === TESTED_OUT,
            );

            return {
                studentInfo: getStudentInfo(student),
                data,
                currentComposite,
                nextComposite,
                compositeGrowth: {
                    achievedGrowthRate: get(zone, isWholeYear ? YEAR_GROWTH_RATE : GROWTH_RATE),
                    achievedGrowthRateLevel: getCompositeAchievedGrowthRateLevel(
                        isTestedOutAvailable,
                        currentComposite,
                        nextComposite,
                        zone,
                        isWholeYear,
                    ),
                },
            };
        }),
    }));
